import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiError, eur, itDate } from "@/lib/api";
import { AuthImage } from "@/components/AuthImage";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, Printer, Flame, FileDown } from "lucide-react";

export default function Rapportino() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [r, setR] = useState(null);
  const [msg, setMsg] = useState("");
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get(`/interventions/${id}/report`)
      .then(({ data }) => setR(data))
      .catch((e) => setLoadError(apiError(e.response?.data?.detail)));
  }, [id]);

  const sendMail = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post(`/interventions/${id}/send-report`);
      setMsg(`Rapportino inviato a ${data.sent_to}.`);
    } catch (e) {
      setMsg(apiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const downloadPdf = async () => {
    try {
      const { data } = await api.get(`/interventions/${id}/report.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rapportino-${r?.code || id}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setMsg("Download del PDF non riuscito. Riprova o rimuovi eventuali foto danneggiate.");
    }
  };

  if (loadError)
    return (
      <div className="p-10" data-testid="rapportino-error">
        <p className="overline">Accesso negato</p>
        <p className="text-sm text-destructive mt-2">{loadError}</p>
        <Button variant="outline" className="rounded-sm mt-4" onClick={() => navigate("/interventi")}>
          Torna agli interventi
        </Button>
      </div>
    );
  if (!r) return <p className="overline p-10">Caricamento…</p>;

  const Row = ({ label, value }) => (
    <div className="flex justify-between gap-4 py-1.5 border-b border-border text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-right">{value || "—"}</span>
    </div>
  );

  return (
    <div className="min-h-screen bg-muted/40 py-6 px-4 print:bg-white print:p-0" data-testid="rapportino-page">
      <div className="max-w-3xl mx-auto flex flex-wrap gap-2 mb-6 print:hidden">
        <Button variant="outline" className="rounded-sm h-11" onClick={() => navigate(`/interventi/${id}`)}
                data-testid="rapportino-back-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Torna all'intervento
        </Button>
        <Button className="rounded-sm h-11" onClick={() => window.print()} data-testid="print-report-btn">
          <Printer className="h-4 w-4 mr-2" /> Stampa
        </Button>
        <Button variant="outline" className="rounded-sm h-11" onClick={downloadPdf} data-testid="download-pdf-btn">
          <FileDown className="h-4 w-4 mr-2" /> Scarica PDF
        </Button>
        <Button variant="outline" className="rounded-sm h-11" onClick={sendMail} disabled={busy}
                data-testid="email-report-btn">
          <Mail className="h-4 w-4 mr-2" /> {busy ? "Invio…" : "Invia al cliente via email"}
        </Button>
      </div>
      {msg && (
        <p className="max-w-3xl mx-auto mb-4 text-sm font-medium text-primary print:hidden"
           data-testid="rapportino-message">{msg}</p>
      )}

      <div className="max-w-3xl mx-auto bg-card border border-border p-8 sm:p-10 print:border-0">
        <div className="flex items-start justify-between gap-6 pb-6 border-b-2 border-foreground">
          <div>
            <div className="flex items-center gap-2">
              <Flame className="h-5 w-5 text-primary" strokeWidth={2.5} />
              <span className="font-display font-black tracking-tighter text-lg">FIREGUARD ANTINCENDIO</span>
            </div>
            <p className="overline mt-2">Rapportino di intervento</p>
          </div>
          <div className="text-right">
            <p className="font-mono text-sm">{r.code}</p>
            <p className="text-xs text-muted-foreground mt-1">{itDate(r.scheduled_date)} {r.scheduled_time}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 gap-x-10 gap-y-1 mt-6">
          <Row label="Cliente" value={r.customer_name} />
          <Row label="P.IVA / CF" value={r.customer_vat} />
          <Row label="Impianto" value={r.plant_name} />
          <Row label="Indirizzo" value={`${r.address || "—"}, ${r.city || "—"}`} />
          <Row label="Tipo intervento" value={r.type} />
          <Row label="Stato" value={r.status?.replace("_", " ")} />
          <Row label="Tecnico" value={r.technician_name} />
          <Row label="Regime" value={r.under_contract ? "In contratto" : "Fuori contratto"} />
        </div>

        <h2 className="font-display text-lg font-bold tracking-tight mt-8 mb-2">Attività svolte</h2>
        <p className="text-sm whitespace-pre-line border-l-2 border-primary pl-3" data-testid="rapportino-work-done">
          {r.work_done || r.description || "—"}
        </p>

        <h2 className="font-display text-lg font-bold tracking-tight mt-8 mb-2">Ricambi utilizzati</h2>
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              <th className="overline font-bold pb-2">Descrizione</th>
              <th className="overline font-bold pb-2 text-center">Q.tà</th>
              <th className="overline font-bold pb-2 text-right">Importo</th>
            </tr>
          </thead>
          <tbody>
            {(r.parts || []).map((p) => (
              <tr key={p.id} className="border-b border-border">
                <td className="py-2">{p.name}</td>
                <td className="py-2 text-center font-mono">{p.quantity}</td>
                <td className="py-2 text-right font-mono">
                  {p.chargeable ? eur(p.quantity * p.unit_price) : "incluso"}
                </td>
              </tr>
            ))}
            {(r.parts || []).length === 0 && (
              <tr><td colSpan={3} className="py-2 text-muted-foreground">Nessun ricambio utilizzato</td></tr>
            )}
          </tbody>
        </table>

        <div className="grid sm:grid-cols-2 gap-10 mt-8">
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight mb-2">Riepilogo economico</h2>
            <Row label={`Manodopera (${r.hours || 0} h)`} value={eur(r.totals?.labour)} />
            <Row label="Ricambi addebitati" value={eur(r.totals?.parts_total)} />
            <Row label="Diritto di chiamata" value={eur(r.totals?.travel)} />
            <div className="flex justify-between pt-3">
              <span className="overline">Totale</span>
              <span className="font-display text-2xl font-black tracking-tighter" data-testid="rapportino-total">
                {eur(r.totals?.total)}
              </span>
            </div>
          </div>
          <div>
            <h2 className="font-display text-lg font-bold tracking-tight mb-2">Firma cliente</h2>
            {r.signature_path ? (
              <AuthImage path={r.signature_path} alt="Firma cliente"
                         className="h-24 w-full object-contain bg-white border-b border-foreground"
                         testid="rapportino-signature" />
            ) : (
              <div className="border-b border-foreground h-16" />
            )}
            <p className="text-sm mt-2">{r.signature_name || "—"}</p>
          </div>
        </div>

        {(r.photos || []).length > 0 && (
          <div className="mt-8">
            <h2 className="font-display text-lg font-bold tracking-tight mb-3">Documentazione fotografica</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {r.photos.map((p) => (
                <figure key={p.id}>
                  <AuthImage path={p.storage_path} alt={p.phase}
                             className="h-28 w-full object-cover border border-border"
                             testid={`rapportino-photo-${p.id}`} />
                  <figcaption className="overline mt-1">{p.phase}</figcaption>
                </figure>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
